import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../redux/modules/user/actions';
import ConfirmationModalNew from '../common/ConfirmationModalNew';
import PrivacyPolicyContent from '../my-profile/legal/PrivacyPolicyContent';
import TermsOfUseContent from '../my-profile/legal/TermsOfUseContent';

const CompliancePopup = ({ open, handleClose, handleAgree, pharmacyName, termsEffectiveDate, privacyEffectiveDate, }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const displayPharmacyName = pharmacyName || 'your pharmacy';
    const [pdfPath, setPdfPath] = useState(null);
    const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
    const [showTermsOfUse, setShowTermsOfUse] = useState(false);

    const renderPrivacyPolicyContent = () => (
        <div className="px-2">
            <PrivacyPolicyContent
                effectiveDate={privacyEffectiveDate}
                handleAgree={() => setShowPrivacyPolicy(false)}
                handleDecline={() => setShowPrivacyPolicy(false)}
                showFooter={false}
                isPopupVersion={true}
            />
        </div>
    );

    const renderTermsOfUseContent = () => (
        <div className="px-2">
            <TermsOfUseContent
                effectiveDate={termsEffectiveDate}
                handleAgree={() => setShowTermsOfUse(false)}
                handleDecline={() => setShowTermsOfUse(false)}
                showFooter={false}
                isPopupVersion={true}
            />
        </div>
    );

    const handleCancelAndLogout = async () => {
        if (handleClose) {
            handleClose();
        }
        try {
            await dispatch(logout());
            navigate('/login');
        } catch (error) {
            navigate('/login');
        }
    };

    const content = () => (
        <div className="text-left">
            <p className="text-[16px] font-normal leading-[24px] text-[#424242]">
                I confirm, I am authorized to access healthcompassRx on behalf of 
            </p>
            <p className="text-[16px] font-medium leading-[24px] text-teal-300 mb-5"> {displayPharmacyName}.</p>

            <p className="text-[16px] font-normal leading-[24px] text-[#424242] mb-5">
                I am a healthcare practitioner acting within the patient’s circle of care.
            </p>

            <p className="text-[16px] font-normal leading-[24px] text-[#424242] mb-5 break-normal">
               By selecting <span className="text-[16px] font-medium leading-[24px] text-teal-300">“I Agree & Continue”</span>, I have agreed to accept and comply with the{" "}
                <button
                    type="button"
                    onClick={() => setShowTermsOfUse(true)}
                    className="inline underline decoration-[1px] text-[16px] leading-[24px] text-teal-300 font-medium cursor-pointer hover:text-teal-300 p-0 m-0 bg-transparent border-0 align-baseline"
                >
                    Terms of Use
                </button>{" "}
                and{" "}
                <button
                    type="button"
                    onClick={() => setShowPrivacyPolicy(true)}
                    className="inline underline decoration-[1px] text-[16px] leading-[24px] text-teal-300 font-medium cursor-pointer hover:text-teal-300 p-0 m-0 bg-transparent border-0 align-baseline"
                >
                    Privacy Policy
                </button>{" "}
                to use this application in accordance
                <br />
                with PHIPA & PIPEDA and HIPPA legislation.
            </p>
            <p className="text-[16px] font-normal leading-[24px] text-[#424242]" >
                Session activity may be monitored for security compliance.
            </p>
        </div>
    );

    return (
        <>
            <ConfirmationModalNew
                open={open}
                title="Compliance Requirements"
                content={content}
                handleClose={handleClose}
                onCross={handleCancelAndLogout}
                confirmText="I Agree & Continue"
                confirmAction={handleAgree}
                type="primary"
                hideActions={false}
                hideCloseIcon={false}
                cancelText={"Cancel & Sign Out"}
                cancelAction={handleCancelAndLogout}
                cancelBtnType="primary-outline"
                actionsClassName="!flex !justify-center !px-[25px]"
                buttonClassName="!w-[300px] !h-[52px]"
                confirmButtonClassName="!font-normal"
                titleClassName="!text-[24px]"
                contentClassName="pt-0 pb-8"
                contentWrapperClassName="!px-[25px]"
                className="!w-[630px] !min-w-[630px] !max-w-[630px] !max-h-[80vh]"
                headClassName='!bg-teal-25 !px-[25px]'
                hideHR={true}
                maskClosable={false}
            />
            {pdfPath && (
                <ConfirmationModalNew
                    open={true}
                    content={() =>
                        <>
                            <object
                                data={pdfPath}
                                type="application/pdf"
                                width={"100%"}
                                className="h-[80vh]"
                            >
                                <p className="font-medium text-gray-800 text-body-md">
                                    Your browser does not support PDFs.{' '}
                                    <a
                                        href={pdfPath}
                                        className="text-blue-600 underline"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Download the PDF
                                    </a>
                                </p>
                            </object>
                        </>
                    }
                    onCross={() => setPdfPath(null)}
                    hideActions={true}
                    setWideModal={true}
                    hideHR={true}
                />
            )}
            {(showPrivacyPolicy || showTermsOfUse) && (
                <ConfirmationModalNew
                    open={showPrivacyPolicy || showTermsOfUse}
                    title={showPrivacyPolicy ? "Privacy Policy" : "Terms Of Use"}
                    // titleClassName="sr-only"
                    content={showPrivacyPolicy ? renderPrivacyPolicyContent : renderTermsOfUseContent}
                    handleClose={() => {
                        if (showPrivacyPolicy) setShowPrivacyPolicy(false);
                        if (showTermsOfUse) setShowTermsOfUse(false);
                    }}
                    onCross={() => {
                        if (showPrivacyPolicy) setShowPrivacyPolicy(false);
                        if (showTermsOfUse) setShowTermsOfUse(false);
                    }}
                    hideCloseIcon={false}
                    hideActions={true}
                    setWideModal={true}
                    hideHR={true}
                    maskClosable={false}
                    className="!gap-0 !pt-0 !max-w-[950px] !w-[950px] !min-w-[950px] !max-h-[85vh]"
                    headClassName="!bg-teal-25 rounded-t-[4px] relative [&>div:last-child]:!top-1/2 [&>div:last-child]:!-translate-y-1/2"
                />
            )}
        </>
    );
};

export default CompliancePopup;
